// File: src/pages/targets/CreateCustomerTarget.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import Multiselect from "multiselect-react-dropdown";
import { useNavigate, useParams } from "react-router-dom";

import {
  ProductListView,
  CreateCustomerList1,
  _Get,
  _GetList,
  _PostSave,
  Create_TargetCustomersave, // uses this as the main api point: Create_CustomerTarget_save = "/target-creation/customer-target";
} from "../import/ApiEndPoint/ApiCalling";

import {
  Create_Account_List,
  Create_CustomerTarget_save, // api: Create_CustomerTarget_save = "/target-creation/customer-target";
  View_CustomerPurchasedQty,
  All_Users_List,
  view_create_order_history,
} from "../import/ApiEndPoint/Api";

/* ------------------ helpers ------------------ */
const lc = (s) =>
  String(s || "")
    .trim()
    .toLowerCase();
const normPin = (v) => {
  const s = String(v ?? "").replace(/\D/g, "");
  return s.length ? s.padStart(6, "0") : "";
};
const pickFirst = (...vals) => {
  for (const v of vals)
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  return "";
};
const getPartyPincode = (party) =>
  normPin(
    pickFirst(
      party?.Pincode,
      party?.pincode,
      party?.PostalCode,
      party?.postalCode,
      party?.Zip,
      party?.zip,
      party?.BillingAddress?.Pincode,
      party?.BillingAddress?.pincode,
      party?.billingAddress?.Pincode,
      party?.billingAddress?.pincode,
      party?.ShippingAddress?.Pincode,
      party?.ShippingAddress?.pincode,
      party?.shippingAddress?.Pincode,
      party?.shippingAddress?.pincode,
      party?.Address?.Pincode,
      party?.Address?.pincode,
    ),
  );
const getPartyLocation = (party) => {
  const city = pickFirst(
    party?.City,
    party?.city,
    party?.BillingAddress?.City,
    party?.billingAddress?.city,
    party?.ShippingAddress?.City,
    party?.shippingAddress?.city,
  );
  const district = pickFirst(
    party?.District,
    party?.district,
    party?.BillingAddress?.District,
    party?.billingAddress?.district,
  );
  const state = pickFirst(
    party?.State,
    party?.state,
    party?.BillingAddress?.State,
    party?.billingAddress?.state,
  );
  return { city: lc(city), district: lc(district), state: lc(state) };
};
const entryPin = (srv) =>
  normPin(pickFirst(srv?.pincode, srv?.PinCode, srv?.pin, srv?.zip));
const entryCity = (srv) => lc(pickFirst(srv?.city, srv?.City));
const entryDistrict = (srv) => lc(pickFirst(srv?.district, srv?.District));
const entryState = (srv) => lc(pickFirst(srv?.state, srv?.State));
const getUserDisplayName = (u) => {
  const fn = u?.firstName || u?.name || "";
  const ln = u?.lastName || "";
  return `${fn} ${ln}`.trim() || "—";
};
const resolveSalesPersonFromService = (party, allUsers) => {
  if (!party) return "";
  const salesPersons = (allUsers || []).filter(
    (u) => u?.rolename?.roleName === "Sales Person" && u?.status === "Active",
  );
  if (!salesPersons.length) return "";
  const pin = getPartyPincode(party);
  const loc = getPartyLocation(party);

  if (pin) {
    for (const sp of salesPersons) {
      const service = Array.isArray(sp?.service) ? sp.service : [];
      if (service.some((s) => entryPin(s) && entryPin(s) === pin)) {
        return sp?._id || "";
      }
    }
  }

  const hasLoc = loc.city || loc.district || loc.state;
  if (hasLoc) {
    for (const sp of salesPersons) {
      const service = Array.isArray(sp?.service) ? sp.service : [];
      if (
        service.some((s) => {
          const c = entryCity(s);
          const d = entryDistrict(s);
          const st = entryState(s);
          const cityOk = loc.city ? c === loc.city : true;
          const distOk = loc.district ? d === loc.district : true;
          const stateOk = loc.state ? st === loc.state : true;
          return cityOk && distOk && stateOk;
        })
      ) {
        return sp?._id || "";
      }
    }
  }
  return "";
};

// FY helpers
const FY_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];
const currentFYLabel = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth(); // 0=Jan
  const start = m >= 3 ? y : y - 1; // FY starts in April (3)
  const endShort = String((start + 1) % 100).padStart(2, "0");
  return `${start}-${endShort}`; // e.g., "2025-26"
};
const monthYearForFY = (monthName, fyLabel) => {
  const startYear = parseInt(String(fyLabel).split("-")[0], 10);
  if (!Number.isFinite(startYear)) return "";
  const endYear = startYear + 1;
  const lastQuarter = ["January", "February", "March"];
  const year = lastQuarter.includes(monthName) ? endYear : startYear;
  return `${monthName}-${year}`;
};

const CreateCustomerTarget = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();

  const [Loading, setLoading] = useState(false);
  const [ProductList, setProductList] = useState([]);
  const [UserList, setUserList] = useState([]);

  const [AllUsers, setAllUsers] = useState([]);
  const [Salesperson, setSalesperson] = useState({}); // selected party/customer

  // FY-wise month selection
  const [financialYear, setFinancialYear] = useState(currentFYLabel());
  const [monthName, setMonthName] = useState("");

  const [incrementPercent, setIncrementPercent] = useState(10);

  const [resolvedSalesPersonId, setResolvedSalesPersonId] = useState("");
  const [resolvedSalesPerson, setResolvedSalesPerson] = useState(null);

  // file upload modal
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState(null);

  // row-wise target & achieved maps
  const [targetQtyMap, setTargetQtyMap] = useState({});
  const [achievedMap, setAchievedMap] = useState({});

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("userData") || "{}"),
    [],
  );

  /* ---------- Bootstrap lists ---------- */
  useEffect(() => {
    if (!userData?._id || !userData?.database) return;

    // Parties (customers)
    CreateCustomerList1(userData._id, userData.database)
      .then((res) => {
        const customers = res?.Customer || [];
        setUserList(customers);
        if (paramId) {
          const defaultParty =
            customers.find((c) => c?._id === paramId) ||
            customers.find((c) => c?.sId === paramId);
          if (defaultParty) setSalesperson(defaultParty);
        }
      })
      .catch(() => {});

    // Products
    ProductListView(userData._id, userData.database)
      .then((res) => setProductList(res?.Product || []))
      .catch(() => {});

    // All users (for sales person resolution)
    _Get(All_Users_List, userData?.database)
      .then((res) => {
        const list =
          res?.User || res?.users || res?.adminDetails || res?.data || [];
        setAllUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, [paramId, userData]);

  /* ---------- When party or products change, fetch achieved qty ---------- */
  useEffect(() => {
    if (!Salesperson?._id || !ProductList?.length) return;
    (async () => {
      const draft = {};
      for (const p of ProductList) {
        try {
          const url = `${View_CustomerPurchasedQty}/${Salesperson._id}/${p._id}`;
          const r = await _GetList(url);
          draft[p._id] = Number(r?.qty || 0);
        } catch {
          draft[p._id] = 0;
        }
      }
      setAchievedMap(draft);
    })();
  }, [Salesperson, ProductList]);

  /* ---------- Auto-resolve Assigned Sales Person ---------- */
  useEffect(() => {
    if (Salesperson?._id && AllUsers?.length) {
      const spId = resolveSalesPersonFromService(Salesperson, AllUsers);
      setResolvedSalesPersonId(spId);
      setResolvedSalesPerson(
        spId ? AllUsers.find((u) => u?._id === spId) || null : null,
      );
    } else {
      setResolvedSalesPersonId("");
      setResolvedSalesPerson(null);
    }
  }, [Salesperson, AllUsers]);

  /* ---------- Derived rows & totals ---------- */
  const rows = useMemo(() => {
    return ProductList.map((p) => {
      const price = Number(p?.SalesRate || 0);
      const targetQty = Number(targetQtyMap[p._id] || 0);
      const achieved = Number(achievedMap[p._id] || 0);
      const shortfall = Math.max(targetQty - achieved, 0);
      const total = targetQty * price;
      return {
        _id: p._id,
        sId: p.sId,
        name: p?.Product_Title || "—",
        price,
        achieved,
        targetQty,
        shortfall,
        total,
      };
    });
  }, [ProductList, targetQtyMap, achievedMap]);

  const grandTotal = useMemo(
    () =>
      rows.reduce(
        (sum, r) => sum + (Number.isFinite(r.total) ? r.total : 0),
        0,
      ),
    [rows],
  );

  const setTargetQty = (productId, value) => {
    const v = Math.max(Number(value || 0), 0);
    setTargetQtyMap((m) => ({ ...m, [productId]: v }));
  };

  // Upload (Excel)
  const handleExcelUpload = () => {
    if (!file) return swal("Please upload a file");
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("created_by", userData?._id || "");
    // You can also pass monthName/financialYear here if your upload endpoint supports it.
    Create_TargetCustomersave(formData)
      .then((res) => {
        setLoading(false);
        setModalOpen(false);
        if (res?.status) swal("Target Created Successfully");
      })
      .catch(() => {
        setLoading(false);
        swal("Upload failed", "Please check your file and try again.", "error");
      });
  };

  // Download template (unchanged from your flow) — left intact
  const handleDownload = async () => {
    try {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      if (!u?._id || !u?.database) return;

      if (u?.rolename?.roleName === "SuperAdmin") {
        const url = `${Create_Account_List + u._id}/`;
        const resUsers = await _Get(url, u.database);
        const allAdminDetails = resUsers?.adminDetails || [];

        const salesPersons = allAdminDetails.filter(
          (usr) =>
            usr?.rolename?.roleName === "Sales Person" &&
            usr?.status === "Active",
        );

        const oh = await _Get(view_create_order_history, u?._id, u?.database);
        const completed = (oh?.orderHistory || [])
          .filter((x) => x?.status !== "Deactive")
          .filter((x) => x?.status?.toLowerCase() === "completed");

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        let fyStart, fyEnd;
        if (currentMonth === 3) {
          fyStart = new Date(currentYear - 1, 3, 1);
          fyEnd = new Date(currentYear, 2, 31);
        } else {
          fyStart = new Date(currentYear, 3, 1);
          fyEnd = new Date(currentYear + (currentMonth < 3 ? 0 : 1), 2, 31);
        }
        const monthLimit = currentMonth - 1;

        const currentFYData = completed.filter((ele) => {
          const d = new Date(ele?.date);
          const m = d.getMonth();
          const y = d.getFullYear();
          return (
            d >= fyStart &&
            d <= fyEnd &&
            (y < currentYear || (y === currentYear && m <= monthLimit))
          );
        });

        const monthGet = monthName
          ? monthYearForFY(monthName, financialYear)
          : "";

        const custRes = await CreateCustomerList1(u._id, u.database);
        const allCustomers = custRes?.Customer || [];

        const data = [];
        for (const sp of salesPersons) {
          const sid = sp?.created_by?.sId || "-";
          const matchingUser = allCustomers.filter(
            (usr) => usr?.created_by?._id === sp?._id,
          );

          for (const cust of matchingUser) {
            const customerId = cust?.sId;
            const partyOrders = currentFYData.filter(
              (d) => d?.partyId?.sId === customerId,
            );

            ProductList.forEach((prod) => {
              let totalQty = 0;
              const price = prod?.SalesRate;

              partyOrders.forEach((entry) => {
                entry?.orderItems?.forEach((it) => {
                  if (it?.productId?.sId === prod?.sId)
                    totalQty += Number(it?.qty || 0);
                });
              });

              data.push({
                SalesHead: sid,
                salesPersonId: sp?.sId,
                partyId: customerId,
                productId: prod?.sId || "",
                qtyAssign: totalQty ? totalQty.toFixed(2) : "0",
                price,
                month: monthGet,
                percentage: incrementPercent,
                created_by: u?._id,
                // optional extra columns if you want in your template:
                // financialYear: financialYear,
                // monthName: monthName,
              });
            });
          }
        }

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Assignments");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([buf], { type: "application/octet-stream" }),
          "salesperson_product_assignment.xlsx",
        );
        return;
      }

      // Non-superadmin flow unchanged, but uses FY month string
      const u2 = JSON.parse(localStorage.getItem("userData") || "{}");
      const oh = await _Get(view_create_order_history, u2?._id, u2?.database);
      const completed = (oh?.orderHistory || [])
        .filter((x) => x?.status !== "Deactive")
        .filter((x) => x?.status?.toLowerCase() === "completed");

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      let fyStart, fyEnd;
      if (currentMonth === 3) {
        fyStart = new Date(currentYear - 1, 3, 1);
        fyEnd = new Date(currentYear, 2, 31);
      } else {
        fyStart = new Date(currentYear, 3, 1);
        fyEnd = new Date(currentYear + (currentMonth < 3 ? 0 : 1), 2, 31);
      }
      const monthLimit = currentMonth - 1;

      const currentFYData = completed.filter((ele) => {
        const d = new Date(ele?.date);
        const m = d.getMonth();
        const y = d.getFullYear();
        return (
          d >= fyStart &&
          d <= fyEnd &&
          (y < currentYear || (y === currentYear && m <= monthLimit))
        );
      });

      const custRes = await CreateCustomerList1(u2._id, u2.database);
      const allCustomers = custRes?.Customer || [];

      const salespersonId = u2?.sId;
      const matchingUser = allCustomers.filter(
        (usr) => usr?.created_by?.sId === salespersonId,
      );

      const salesManagerId =
        u2?.rolename?.roleName === "SuperAdmin" ? u2?.sId : u2?.created_by?.sId;
      const monthGet = monthName
        ? monthYearForFY(monthName, financialYear)
        : "";

      const data = [];
      for (const cust of matchingUser) {
        const sid = cust?.sId;
        const partyOrders = currentFYData.filter(
          (d) => d?.partyId?.sId === sid,
        );

        ProductList.forEach((prod) => {
          let totalQty = 0;
          const price = prod?.SalesRate;

          partyOrders.forEach((entry) => {
            entry?.orderItems?.forEach((it) => {
              if (it?.productId?.sId === prod?.sId)
                totalQty += Number(it?.qty || 0);
            });
          });

          data.push({
            SalesHead: salesManagerId,
            salesPersonId: cust?.created_by?.sId,
            partyId: sid,
            productId: prod?.sId || "",
            qtyAssign: totalQty ? totalQty.toFixed(2) : "0",
            price,
            month: monthGet,
            percentage: incrementPercent,
            created_by: u2?._id,
          });
        });
      }

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Assignments");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        "salesperson_product_assignment.xlsx",
      );
    } catch (err) {
      console.log(err);
      swal("Download failed", "Unable to build the template.", "error");
    }
  };

  /* ---------- Submit (sends salesPersonName + partyName + FY-aware date) ---------- */
  const submitHandler = async (e) => {
    e.preventDefault();
    if (!Salesperson?._id) return swal("Error", "Choose Customer", "error");
    if (!monthName) return swal("Error", "Choose Target Month", "error");
    if (!financialYear) return swal("Error", "Choose Financial Year", "error");

    setLoading(true);

    const resolvedSalesName = resolvedSalesPerson
      ? getUserDisplayName(resolvedSalesPerson)
      : "Not Mapped";

    const products = rows
      .filter((r) => Number(r.targetQty) > 0)
      .map((r) => ({
        productId: r._id,
        qtyAssign: Number(r.targetQty),
        price: Number(r.price),
        totalPrice: Number((r.price * r.targetQty).toFixed(2)),
        assignPercentage: [],
      }));

    const partyName =
      Salesperson?.CompanyName ||
      Salesperson?.companyName ||
      Salesperson?.name ||
      "—";

    const payload = {
      // FY-aware save:
      monthName, // e.g., "April"
      financialYear, // e.g., "2025-26"
      date: monthYearForFY(monthName, financialYear), // e.g., "April-2026"

      partyId: Salesperson?._id,
      partyName, // ✅ save human-readable customer name

      products,
      created_by: userData?._id,

      salesPersonId: resolvedSalesPersonId || "",
      salesPersonName: resolvedSalesName, // ✅ save human-readable SP name

      incrementPercent: Number(incrementPercent) || 0,
      grandTotal: Number(grandTotal.toFixed(2)),
    };

    try {
      const res = await _PostSave(Create_CustomerTarget_save, payload);
      setLoading(false);
      if (res?.status) {
        swal("Target Created Successfully");
        navigate(-1);
      } else {
        swal("Error", res?.message || "Failed to save target", "error");
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
      swal("Error", "Failed to save target", "error");
    }
  };

  // Build FY list (current FY ± 2)
  const fyOptions = useMemo(() => {
    const cur = parseInt(currentFYLabel().split("-")[0], 10);
    const arr = [];
    for (let y = cur - 2; y <= cur + 2; y++) {
      const endShort = String((y + 1) % 100).padStart(2, "0");
      arr.push(`${y}-${endShort}`);
    }
    return arr.reverse();
  }, []);

  return (
    <div>
      <Card>
        <Row className="m-2">
          <Col>
            <h1>Create Customer Target</h1>
          </Col>
          <Col className="d-flex justify-content-end" style={{ gap: 8 }}>
            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button color="primary" onClick={() => setModalOpen(true)}>
              Upload
            </Button>
            <Button color="primary" onClick={handleDownload}>
              Download
            </Button>
          </Col>
        </Row>

        <CardBody>
          <Form className="m-1" onSubmit={submitHandler}>
            <Row>
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Choose Party</Label>
                <Multiselect
                  required
                  selectionLimit={1}
                  isObject
                  options={UserList}
                  onSelect={(list, item) => setSalesperson(item)}
                  onRemove={() => setSalesperson({})}
                  displayValue="CompanyName"
                />
              </Col>

              {/* Read-only Assigned Sales Person */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Assigned Sales Person</Label>
                <Input
                  type="text"
                  readOnly
                  disabled
                  value={
                    resolvedSalesPerson
                      ? getUserDisplayName(resolvedSalesPerson)
                      : Salesperson?._id
                        ? "Not Mapped"
                        : ""
                  }
                />
              </Col>

              {/* Financial Year */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Financial Year</Label>
                <Input
                  type="select"
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                >
                  <option value="">Select FY</option>
                  {fyOptions.map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Month (FY order) */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Target Month (FY)</Label>
                <Input
                  type="select"
                  value={monthName}
                  onChange={(e) => setMonthName(e.target.value)}
                >
                  <option value="">Select Month</option>
                  {FY_MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Increment % */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Enter Increment %</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="e.g. 10"
                  value={incrementPercent}
                  onChange={(e) => setIncrementPercent(e.target.value)}
                />
              </Col>
            </Row>

            {/* ROW-WISE PRODUCTS */}
            <div className="table-responsive mt-2">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Product</th>
                    <th>Achieved Qty</th>
                    <th>Target Qty</th>
                    <th>Shortfall Qty</th>
                    <th>Price (SalesRate)</th>
                    <th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r._id}>
                      <td>{r.name}</td>
                      <td>
                        <Input type="number" readOnly value={r.achieved} />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          value={r.targetQty}
                          onChange={(e) => setTargetQty(r._id, e.target.value)}
                        />
                      </td>
                      <td>
                        <Input type="number" readOnly value={r.shortfall} />
                      </td>
                      <td>
                        <Input type="number" readOnly value={r.price} />
                      </td>
                      <td>
                        <Input
                          type="number"
                          readOnly
                          value={r.total.toFixed(2)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={5} className="text-right">
                      Grand Total
                    </th>
                    <th>
                      <Input
                        type="number"
                        readOnly
                        value={grandTotal.toFixed(2)}
                      />
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Row className="mt-2">
              <Col className="d-flex justify-content-center">
                <Button color="primary" type="submit" disabled={Loading}>
                  {Loading ? "Loading..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      {/* Upload modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Upload Excel File
        </ModalHeader>
        <ModalBody>
          <Input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleExcelUpload}
            disabled={Loading}
          >
            {Loading ? "Uploading..." : "Submit"}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CreateCustomerTarget;
